import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake } from "lucide-react";
import { api, API } from "@/lib/api";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const MESES_SHORT = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

function initials(nome = "") {
  const semTitulo = nome.replace(/^Rev\.?\s*(Pe\.?|Diác\.?)?\s*/i, "").replace(/^Dom\s+/i, "");
  return semTitulo.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function ClergyBirthdays() {
  const [items, setItems] = useState(null);
  const agora = new Date();
  const mesAtual = agora.getMonth() + 1;
  const diaAtual = agora.getDate();
  const [mesSel, setMesSel] = useState(mesAtual);

  useEffect(() => {
    api.get("/clergy/aniversariantes").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const porMes = useMemo(() => {
    const map = new Map();
    (items || []).forEach((i) => {
      if (!map.has(i.mes)) map.set(i.mes, []);
      map.get(i.mes).push(i);
    });
    return map;
  }, [items]);

  if (!items || items.length === 0) return null;

  const deHoje = items.filter((i) => i.dia === diaAtual && i.mes === mesAtual);
  const lista = porMes.get(mesSel) || [];

  return (
    <section id="aniversariantes" data-testid="birthdays-section" className="bg-[#F4EFE6] px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
        <motion.p variants={fadeUp} className="overline-label text-[#4A0E17]/60">Calendário do Clero</motion.p>
        <motion.h2 variants={fadeUp} className="font-display mt-4 text-4xl font-bold uppercase leading-tight text-[#1C1917] sm:text-5xl">
          Aniversariantes do Clero
        </motion.h2>
        <motion.p variants={fadeUp} className="font-liturgy mt-4 max-w-2xl text-xl italic leading-relaxed text-[#1C1917]/65">
          Celebramos com alegria e gratidão o dom da vida dos nossos clérigos.
        </motion.p>

        {deHoje.length > 0 && (
          <motion.div variants={fadeUp} data-testid="birthday-today-banner" className="mt-8 flex flex-wrap items-center gap-4 border border-[#C5A059] bg-[#2E070D] px-6 py-5">
            <Cake className="h-7 w-7 shrink-0 text-[#C5A059]" strokeWidth={1.5} />
            <p className="font-display text-lg font-bold text-[#FBF9F4] sm:text-xl">
              Hoje celebramos a vida de{" "}
              {deHoje.map((c, i) => (
                <span key={c.slug}>
                  {i > 0 && " e "}
                  <Link to={`/clero/${c.slug}`} className="text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 transition-colors hover:decoration-[#C5A059]">
                    {c.nome}
                  </Link>
                </span>
              ))}
            </p>
          </motion.div>
        )}

        <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Meses do ano">
          {MESES_SHORT.map((m, idx) => {
            const mes = idx + 1;
            const qtd = (porMes.get(mes) || []).length;
            const active = mes === mesSel;
            return (
              <button
                key={m}
                role="tab"
                aria-selected={active}
                disabled={qtd === 0}
                data-testid={`birthday-month-${mes}`}
                onClick={() => setMesSel(mes)}
                className={`overline-label border px-4 py-2 transition-colors ${
                  active
                    ? "border-[#C5A059] bg-[#4A0E17] text-[#FBF9F4]"
                    : qtd === 0
                      ? "cursor-not-allowed border-[#E5DEC9] text-[#1C1917]/25"
                      : "border-[#E5DEC9] text-[#4A0E17] hover:border-[#C5A059]"
                } ${mes === mesAtual && !active ? "border-[#C5A059]/70" : ""}`}
              >
                {m}
                {qtd > 0 && <span className="ml-1.5 text-[#C5A059]">{qtd}</span>}
              </button>
            );
          })}
        </motion.div>

        <motion.div variants={fadeUp} className="mt-8">
          <p className="overline-label mb-5 text-[#4A0E17]/60" data-testid="birthday-month-label">
            {MESES[mesSel - 1]}{mesSel === mesAtual ? " — mês atual" : ""}
          </p>
          {lista.length === 0 ? (
            <p className="text-sm text-[#1C1917]/50">Nenhum aniversariante cadastrado neste mês.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="birthday-list">
              {lista.map((c) => (
                <Link
                  key={c.slug}
                  to={`/clero/${c.slug}`}
                  data-testid={`birthday-card-${c.slug}`}
                  className="group flex min-w-0 items-center gap-4 border border-[#E5DEC9] bg-[#FBF9F4] p-5 transition-colors hover:border-[#C5A059]"
                >
                  <span className="flex h-14 w-14 shrink-0 flex-col items-center justify-center bg-[#4A0E17] font-display text-[#C5A059]">
                    <span className="text-xl font-bold leading-none">{String(c.dia).padStart(2, "0")}</span>
                    <span className="mt-1 text-[9px] uppercase tracking-widest">{MESES_SHORT[c.mes - 1]}</span>
                  </span>
                  {c.foto ? (
                    <img
                      src={`${API}/files/${c.foto}`}
                      alt={`Fotografia oficial de ${c.nome}`}
                      loading="lazy"
                      className="h-14 w-14 rounded-full border border-[#C5A059]/40 object-cover"
                    />
                  ) : (
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#C5A059]/40 bg-[#2E070D] font-display text-sm font-bold text-[#C5A059]">
                      {initials(c.nome)}
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="font-display block truncate text-base font-bold text-[#1C1917] transition-colors group-hover:text-[#4A0E17]">{c.nome}</span>
                    <span className="mt-0.5 block truncate text-xs text-[#1C1917]/55">{c.prelazia || "DACB"}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    </section>
  );
}
